import { useMutateMonitoringAssortmentItems } from '../monitoringAssortmentApi';
import { useSelectedCompetitors } from '@retail/retailer/data-access';
import { ProductsTFunction } from '@retail/products/i18n';
import { generateXLSX, XLSXRow } from '@retail/products/utils';
import { MonitoringAssortmentItem } from '@retail/monitoring-assortment/types';
import { useCallback, useMemo } from 'react';
import { useDisclosure } from '@shared/hooks';
import { useToast } from '@retail/hooks';
import dayjs from 'dayjs';
import { useAppTFunction } from '@retail/app/i18n';
import { fetchPaginatedDataInParallel } from '@shared/fetch-utils';
import { useFilters } from '@retail/products/hooks';

interface Props {
  assortmentId: number;
  assortmentName: string;
}

const getStaticColumns = (t: ProductsTFunction) => [
  t('products.mgItem.supplierItem.primaryText'),
  t('products.mgItem.number.label'),
  t('products.mgItem.supplierItem.finfoNumber.label'),
  t('products.mgItem.supplierItem.primaryGtin'),
  t('products.mgItem.supplierItem.salesPrice.title'),
  t('products.mgItem.supplierItem.unit'),
];

const getCompetitorColumns = (t: ProductsTFunction) => [
  t('products.mgItem.supplierItem.salesPrice.title'),
  t('products.mgItem.labels.priceDate'),
];

const formatNullableDate = (date: string | null | undefined) => {
  if (!date) return null;
  return dayjs(date).format('YYYY-MM-DD');
};

export const useExportMonitoringAssortmentToExcel = ({ assortmentId, assortmentName }: Props) => {
  const t = useAppTFunction();
  const toast = useToast();

  const { isOpen: isLoading, onOpen: onLoadingStart, onClose: onLoadingEnd } = useDisclosure(false);

  const { mutateAsync: loadDataForExport } = useMutateMonitoringAssortmentItems();
  const competitors = useSelectedCompetitors();
  const { filters } = useFilters(['productGroupNumbers', 'searchQuery']);

  const exportToExcel = useCallback(async () => {
    onLoadingStart();

    try {
      const pageSizeChunk = 10000;

      const allItems = await fetchPaginatedDataInParallel({
        fetchFn: ({ page }) =>
          loadDataForExport({
            urlVariables: {
              assortmentId,
              competitorStoreIds: competitors.map(({ id }) => id),
              nCmpPrices: 2,
              groupNumbers: filters.productGroupNumbers,
              query: filters.searchQuery ?? undefined,
              size: pageSizeChunk,
              page,
            },
          }),
      });

      const dataRows: XLSXRow[] = allItems
        .map((item) => new MonitoringAssortmentItem(item))
        .map((item) => {
          const primaryOrLowestSalesPriceSupplierItem =
            item.getPrimaryOrLowestSalesPriceSupplierItem();
          const fPakUnitPrice =
            primaryOrLowestSalesPriceSupplierItem?.currentSalesPrices?.getBracketPackagePrice(
              'F-PAK'
            );
          const salesPrice =
            fPakUnitPrice?.bracketMarkupPrice.getPriceForClassCode('F-PAK')?.price.priceWithVat;

          const staticCells = [
            primaryOrLowestSalesPriceSupplierItem?.primaryText,
            item.mgItemNumber,
            primaryOrLowestSalesPriceSupplierItem?.finfoNumber ?? null,
            primaryOrLowestSalesPriceSupplierItem?.currentSalesPrices?.getBracketPackagePrice(
              'F-PAK'
            )?.bracketMarkupPrice.calculationPackage.gtinCode,
            salesPrice ?? null,
            primaryOrLowestSalesPriceSupplierItem?.primaryPackageUnit ?? null,
          ];

          const competitorCells = competitors.flatMap(({ id }) => {
            const currentPrice = item.getCurrentCompetitorPrice(id);
            const currentDefaultPrice = currentPrice?.getPrimaryUnitPrice();

            return [
              currentDefaultPrice?.price ?? null,
              formatNullableDate(currentPrice?.lastObservedAt),
            ];
          });

          return [...staticCells, ...competitorCells];
        });

      const headerRow: XLSXRow = getStaticColumns(t);
      const competitorColumns: XLSXRow = competitors.flatMap((comp) =>
        getCompetitorColumns(t).map((col) => `${comp.name} ${col}`)
      );

      await generateXLSX({
        rows: [headerRow.concat(competitorColumns), ...dataRows],
        fileName: `${assortmentName} ${dayjs().format('YYYY-MM-DD')}`,
      });
      toast.success(t('export.messages.fileDownloaded'));
    } catch (err: any) {
      console.error(err);
      toast.error(t('products.errors.generic'));
    }

    onLoadingEnd();
  }, [
    assortmentId,
    assortmentName,
    competitors,
    filters.productGroupNumbers,
    filters.searchQuery,
    loadDataForExport,
    onLoadingEnd,
    onLoadingStart,
    t,
    toast,
  ]);

  return useMemo(
    () => ({
      isLoading,
      exportToExcel,
    }),
    [exportToExcel, isLoading]
  );
};
